// services/imageSearch.js
//
// Home → search by photo. A photo (camera or gallery) is compared with every Home cake two ways:
//   • looks:   a 64-bit perceptual hash (dHash), the same one the social worker stores in
//              imageIndex — near-identical photos score highest;
//   • meaning: OneAdmin AI (app-ai "cake-tags") describes the photo as search tags (unicorn,
//              drip, pink, two-tier, birthday …), matched against each cake's AI tags and its
//              theme / flavour / cover / events / shapes.
// Works without the worker (hashes are then computed here, through the image proxy) and without
// AI (looks only).

import { collection, getDocs, query, where } from 'firebase/firestore';
import { getProdDb } from './firebase';
import { proxiedImageUrl, isFirebaseStorageUrl } from './storageFallback';

const AI_URL = import.meta.env.VITE_ONEADMIN_AI_URL || 'https://anewoiyqrskohxkvbpfr.supabase.co/functions/v1/app-ai';
const AI_KEY = import.meta.env.VITE_ONEADMIN_ANON_KEY || '';
const APP_SLUG = import.meta.env.VITE_ONEADMIN_APP_SLUG || 'pcakefresh';
const HASH_CACHE_KEY = 'pcake_image_hashes_v1';

// ── hashing ────────────────────────────────────────────────────────────────────────────────

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('image not loadable'));
  img.src = src;
});

/** 64×64 RGB(A) pixels → 9×8 area averages → luma → 64 bits as hex. Identical in app and worker. */
function bitsFrom64(px, channels) {
  const grey = new Array(72).fill(0);
  for (let gy = 0; gy < 8; gy++) {
    for (let gx = 0; gx < 9; gx++) {
      const x0 = (gx * 64) / 9, x1 = ((gx + 1) * 64) / 9, y0 = gy * 8, y1 = gy * 8 + 8;
      let sum = 0, area = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = Math.floor(x0); x < Math.ceil(x1); x++) {
          const w = Math.min(x + 1, x1) - Math.max(x, x0);
          if (w <= 0) continue;
          const i = (y * 64 + x) * channels;
          sum += w * (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
          area += w;
        }
      }
      grey[gy * 9 + gx] = sum / area;
    }
  }
  let hex = '';
  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x += 4) {
      let nib = 0;
      for (let k = 0; k < 4; k++) nib = (nib << 1) | (grey[y * 9 + x + k] < grey[y * 9 + x + k + 1] ? 1 : 0);
      hex += nib.toString(16);
    }
  }
  return hex;
}

/**
 * dHash of anything drawable: 64 bits as 16 hex chars (same as the social worker's).
 * `crop` (0..1) hashes only the centre part — a screenshot of a cake has app bars and margins
 * around the photo, which would otherwise move every bit of the hash.
 */
function dhashOf(source, crop = 1) {
  const w = source.width, h = source.height;
  const cw = w * crop, ch = h * crop;
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, (w - cw) / 2, (h - ch) / 2, cw, ch, 0, 0, 64, 64);
  return bitsFrom64(ctx.getImageData(0, 0, 64, 64).data, 4);
}

/**
 * The photo without the plain border around it (a screenshot's app bars, white margins).
 * `busyRow(y)` / `busyCol(x, y0, y1)` decide what counts as "photo"; the photo is the tallest
 * run of busy rows (a bar with a line of text is a short run). Returns a canvas, or null.
 */
function cropBox(source, W, H, busyRow, busyCol) {
  let best = null, start = -1;
  for (let y = 0; y <= H; y++) {
    const busy = y < H && busyRow(y);
    if (busy && start < 0) start = y;
    if (!busy && start >= 0) { if (!best || y - start > best[1] - best[0]) best = [start, y - 1]; start = -1; }
  }
  if (!best || best[1] - best[0] < H * 0.2) return null;
  let x0 = 0, x1 = W - 1;
  while (x0 < x1 && !busyCol(x0, best[0], best[1])) x0++;
  while (x1 > x0 && !busyCol(x1, best[0], best[1])) x1--;
  const sx = source.width / W, sy = source.height / H;
  const box = { x: x0 * sx, y: best[0] * sy, w: (x1 - x0 + 1) * sx, h: (best[1] - best[0] + 1) * sy };
  if (box.w < source.width * 0.2 || (box.w > source.width * 0.97 && box.h > source.height * 0.97)) return null;
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(box.w)); out.height = Math.max(1, Math.round(box.h));
  out.getContext('2d').drawImage(source, box.x, box.y, box.w, box.h, 0, 0, out.width, out.height);
  return out;
}

/** Candidate crops of a screenshot: by "differs from the page colour" and by "detailed". */
function trimmedVariants(source) {
  const W = 160, H = Math.max(16, Math.round((160 * source.height) / source.width));
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(source, 0, 0, W, H);
  const d = ctx.getImageData(0, 0, W, H).data;
  const rgb = (x, y) => { const i = (y * W + x) * 4; return [d[i], d[i + 1], d[i + 2]]; };
  const lum = (x, y) => { const p = rgb(x, y); return 0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2]; };
  const out = [];
  // a) page colour = the most common colour on the left/right edges (bars at the top do not count)
  const edge = [];
  for (let y = 0; y < H; y++) { edge.push(rgb(0, y), rgb(W - 1, y)); }
  const key = (p) => p.map((v) => v >> 4).join(',');
  const counts = new Map();
  edge.forEach((p) => counts.set(key(p), (counts.get(key(p)) || 0) + 1));
  const top = [...counts.entries()].sort((a2, b2) => b2[1] - a2[1])[0]?.[0];
  const bg = edge.find((p) => key(p) === top) || rgb(0, 0);
  const differs = (x, y) => { const p = rgb(x, y); return Math.abs(p[0] - bg[0]) + Math.abs(p[1] - bg[1]) + Math.abs(p[2] - bg[2]) > 48; };
  out.push(cropBox(source, W, H,
    (y) => { let n = 0; for (let x = 0; x < W; x++) if (differs(x, y)) n++; return n > W * 0.3; },
    (x, y0, y1) => { let n = 0; for (let y = y0; y <= y1; y++) if (differs(x, y)) n++; return n > (y1 - y0 + 1) * 0.3; }));
  // b) detail = brightness varies along the row / column
  const spread = (vals) => { const m = vals.reduce((a2, v) => a2 + v, 0) / vals.length; return Math.sqrt(vals.reduce((a2, v) => a2 + (v - m) ** 2, 0) / vals.length); };
  out.push(cropBox(source, W, H,
    (y) => spread(Array.from({ length: W }, (_, x) => lum(x, y))) > 22,
    (x, y0, y1) => spread(Array.from({ length: y1 - y0 + 1 }, (_, k) => lum(x, y0 + k))) > 22));
  return out.filter(Boolean);
}

/** The query photo as several hashes: whole, without its border, and its centre at a few sizes. */
const queryHashes = (source) => {
  const list = [1, 0.9, 0.8, 0.7, 0.6].map((c) => dhashOf(source, c));
  for (const t of trimmedVariants(source)) list.push(dhashOf(t), dhashOf(t, 0.95), dhashOf(t, 0.9));
  return list;
};

export function hashDistance(a, b) {
  if (!a || !b || a.length !== 16 || b.length !== 16) return 64;
  let n = 0;
  for (let i = 0; i < 16; i++) {
    let x = parseInt(a[i], 16) ^ parseInt(b[i], 16);
    while (x) { n += x & 1; x >>= 1; }
  }
  return n;
}

async function fileBitmap(file) {
  if (window.createImageBitmap) {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall back */ }
  }
  const url = URL.createObjectURL(file);
  try { return await loadImage(url); } finally { setTimeout(() => URL.revokeObjectURL(url), 5000); }
}

/** JPEG (base64, ≤1024 px) of the query photo for the AI call. */
function toJpegBase64(source) {
  const max = 1024;
  const w = source.width, h = source.height;
  const s = Math.min(1, max / Math.max(w, h));
  const c = document.createElement('canvas');
  c.width = Math.round(w * s); c.height = Math.round(h * s);
  c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.8).split(',')[1];
}

const readCache = () => { try { return JSON.parse(localStorage.getItem(HASH_CACHE_KEY) || '{}'); } catch { return {}; } };
const writeCache = (m) => { try { localStorage.setItem(HASH_CACHE_KEY, JSON.stringify(m)); } catch { /* full or blocked */ } };

/** Hash of a profile image by URL (via the proxy, which allows canvas reads), cached locally. */
async function hashOfUrl(url, cache) {
  if (cache[url]) return cache[url];
  const src = isFirebaseStorageUrl(url) ? proxiedImageUrl(url) : url;
  const img = await loadImage(src);
  const h = dhashOf(img);
  cache[url] = h;
  return h;
}

// ── AI tags ────────────────────────────────────────────────────────────────────────────────

async function photoTags(base64) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (AI_KEY) { headers.Authorization = `Bearer ${AI_KEY}`; headers.apikey = AI_KEY; }
    const r = await fetch(AI_URL, {
      method: 'POST', headers,
      body: JSON.stringify({ app: APP_SLUG, task: 'cake-tags', image: base64, imageMime: 'image/jpeg', subject: 'home-image-search' }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || d.ok === false) return null;
    return { tags: d.tags || [], colors: d.colors || [], isCake: d.isCake !== false, description: d.description || '' };
  } catch {
    return null;
  }
}

// ── index ──────────────────────────────────────────────────────────────────────────────────

// One cache per index: Home's (everyone) and each person's own Custom cakes.
const indexCache = new Map();
async function homeIndex({ source = 'home', uid = null } = {}) {
  const key = source === 'home' ? 'home' : `custom:${uid}`;
  const hit = indexCache.get(key);
  if (hit && Date.now() - hit.at < 10 * 60_000) return hit.map;
  const map = new Map();
  try {
    // Custom entries are readable by their owner only (firestore.rules), so the query names the uid.
    const q = source === 'home'
      ? query(collection(getProdDb(), 'imageIndex'), where('source', '==', 'home'))
      : query(collection(getProdDb(), 'imageIndex'), where('source', '==', 'custom'), where('uid', '==', uid));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => map.set(d.id, d.data()));
  } catch (e) {
    console.warn('imageIndex not readable (worker not deployed yet?):', e.message);
  }
  indexCache.set(key, { at: Date.now(), map });
  return map;
}

// ── search ─────────────────────────────────────────────────────────────────────────────────

const words = (s) => String(s || '').toLowerCase().split(/[^a-z0-9äöüß]+/i).filter((w) => w.length > 2);

function textOf(profile, entry) {
  const parts = [
    profile.theme, profile.cflavour, profile.cfilling, profile.cover, profile.frosting, profile.sponge,
    ...(Array.isArray(profile.cevent) ? profile.cevent : []), ...(Array.isArray(profile.cshape) ? profile.cshape : []),
    ...(entry?.tags || []), ...(entry?.colors || []), entry?.description,
  ];
  return new Set(parts.flatMap(words));
}

/**
 * @param {File|Blob} file the photo to search with
 * @param {object[]} profiles the profiles currently visible (Home, or the person's Custom page)
 * @param {(msg: string) => void} onProgress
 * @param {{source?: 'home'|'custom', uid?: string}} [scope] which image index to compare with
 * @returns {Promise<{results: {id: string, score: number}[], mode: 'exact'|'similar', tags: string[], usedAi: boolean}>}
 */
export async function searchByImage(file, profiles, onProgress = () => {}, scope = {}) {
  onProgress('Reading your photo…');
  const bitmap = await fileBitmap(file);
  const queryHashList = queryHashes(bitmap);
  const aiPromise = photoTags(toJpegBase64(bitmap));

  onProgress('Comparing with the cakes…');
  const index = await homeIndex(scope.source === 'custom' && scope.uid ? { source: 'custom', uid: scope.uid } : {});
  const cache = readCache();
  const imageOf = (p) => p.cimage || (Array.isArray(p.images) && p.images[0]) || '';

  // Cakes the worker has not indexed yet are hashed here (6 at a time).
  const missing = profiles.filter((p) => imageOf(p) && !(index.get(p.id)?.hash && index.get(p.id)?.url === imageOf(p)));
  const local = new Map();
  for (let i = 0; i < missing.length; i += 6) {
    await Promise.all(missing.slice(i, i + 6).map(async (p) => {
      try { local.set(p.id, await hashOfUrl(imageOf(p), cache)); } catch { /* skip unreadable */ }
    }));
    if (missing.length > 12) onProgress(`Comparing with the cakes… ${Math.min(i + 6, missing.length)}/${missing.length}`);
  }
  writeCache(cache);

  const ai = await aiPromise;
  const queryWords = ai ? new Set([...ai.tags, ...ai.colors].flatMap(words)) : new Set();

  // Near-identical (same photo, resized / recompressed / screenshotted): a few bits apart.
  const EXACT = 8;
  const scored = profiles.map((p) => {
    const entry = index.get(p.id);
    const hash = (entry?.url === imageOf(p) && entry?.hash) || local.get(p.id);
    const dist = hash ? Math.min(...queryHashList.map((q) => hashDistance(q, hash))) : 64;
    let meaning = 0;
    if (queryWords.size) {
      const t = textOf(p, entry);
      let hit = 0;
      queryWords.forEach((w) => { if (t.has(w)) hit++; });
      meaning = Math.min(1, hit / Math.min(queryWords.size, 8));
    }
    return { id: p.id, distance: dist, meaning };
  });

  // 1) Near-identical copies (the photo IS one of the cakes) — listed first.
  const exact = scored.filter((r) => r.distance <= EXACT).sort((a, b) => a.distance - b.distance);
  const exactIds = new Set(exact.map((r) => r.id));

  // 2) Cakes that LOOK like it and/or are the same KIND (AI tags). Strict cut-offs,
  //    and nothing far below the best match, so a photo does not return half the catalogue.
  const ranked = scored.filter((r) => !exactIds.has(r.id)).map((r) => {
    const looks = Math.max(0, 1 - r.distance / 22);           // ≥22 bits apart → no visual credit
    const score = queryWords.size ? 0.45 * looks + 0.55 * r.meaning : looks;
    return { id: r.id, score: Math.round(score * 1000) / 1000, distance: r.distance };
  }).sort((a, b) => b.score - a.score);
  const best = ranked[0]?.score || 0;
  const floor = queryWords.size ? 0.4 : 0.45;               // no AI: roughly ≤12 bits apart
  let similar = ranked.filter((r) => r.score >= floor && r.score >= best * 0.75).slice(0, 12);
  // Never leave the person empty-handed: fall back to the closest few.
  if (!similar.length) similar = ranked.filter((r) => r.score > 0.15).slice(0, 6);

  if (exact.length) {
    return {
      // Exact matches score above 1 so they always sort before the similar ones.
      results: [
        ...exact.map((r) => ({ id: r.id, score: 2 - r.distance / 64, distance: r.distance, exact: true })),
        ...similar,
      ],
      exactCount: exact.length, similarCount: similar.length,
      mode: 'exact', tags: ai?.tags || [], usedAi: !!ai, isCake: ai ? ai.isCake : true,
    };
  }
  const results = similar;

  return { results, exactCount: 0, similarCount: results.length, mode: 'similar', tags: ai?.tags || [], usedAi: !!ai, isCake: ai ? ai.isCake : true };
}
